'use client';

import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { HUB_EVENTS, pushDataLayer } from '@/lib/analytics';
import { useInView } from '@/lib/hooks/useInView';
import Countdown from './countdown';
import type { HubContent } from '@/lib/content/hub';
import type { LockedDrop } from '@/lib/hub/types';

type HeroProps = {
  content: HubContent;
  serverNow: number;
  /** Earliest unrevealed drop, or null once every location is live. */
  nextDrop: LockedDrop | null;
  allRevealed: boolean;
};

function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ''));
}

export default function Hero({ content, serverNow, nextDrop, allRevealed }: HeroProps) {
  const router = useRouter();
  const { hero, countdown } = content;
  const [reachedZero, setReachedZero] = useState(false);
  const { ref: copyRef, inView: copyInView } = useInView<HTMLDivElement>();

  // A new target comes in after each reveal — start its countdown fresh.
  useEffect(() => {
    setReachedZero(false);
  }, [nextDrop?.id]);

  const handleZero = useCallback(() => {
    if (nextDrop) pushDataLayer(HUB_EVENTS.DROP_REVEALED, { id: nextDrop.id });
    setReachedZero(true);
  }, [nextDrop]);

  // Once a drop's countdown hits zero, nudge the server (ISR is up to 30s
  // stale): one refresh after 2s, then every 10s until the server render
  // confirms the reveal — repeats for each remaining drop until all are live.
  useEffect(() => {
    if (!reachedZero || allRevealed) return;
    const first = window.setTimeout(() => router.refresh(), 2_000);
    const poll = window.setInterval(() => router.refresh(), 10_000);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(poll);
    };
  }, [reachedZero, allRevealed, router]);

  // Once every drop is live the countdown itself shows `countdownDone`, so the
  // label above it is hidden rather than repeating it.
  const eventLabel =
    !nextDrop ? null
    : nextDrop.order === 1 ? hero.eventLabel
    : fill(hero.nextReveal, { barrio: nextDrop.barrio });

  return (
    <section id="top" className="hub-section bg-coal pt-[var(--header-h)] lg:pt-0">
      <div className="grid lg:min-h-[100svh] lg:grid-cols-2">
        <div className="relative order-1 overflow-hidden lg:order-2">
          <Image
            src="/hub/hero.jpg"
            alt={hero.imgAlt}
            width={1782}
            height={1800}
            priority
            sizes="(min-width: 1024px) 50vw, 100vw"
            className="aspect-[4/5] size-full object-cover object-top sm:aspect-[16/10] lg:absolute lg:inset-0 lg:aspect-auto"
          />
          <Image
            src="/hub/marks/hero-lockup.png"
            alt=""
            aria-hidden="true"
            width={600}
            height={374}
            className="absolute right-6 bottom-6 w-[140px] sm:right-10 sm:bottom-10 sm:w-[190px]"
          />
        </div>

        <div
          ref={copyRef}
          className={`order-2 flex flex-col justify-center px-5 pt-14 pb-16 transition-all duration-700 ease-out sm:px-10 lg:order-1 lg:px-16 lg:pt-32 lg:pb-20 xl:px-24 ${
            copyInView ? 'translate-x-0 opacity-100' : '-translate-x-12 opacity-0'
          }`}
        >
          <h1 className="hub-display mt-4 text-[clamp(2.4rem,5.6vw,4.4rem)] text-cream">
            {hero.headline[0]}
            <br />
            {hero.headline[1]}
            <br />
            <span className="text-yamal">{hero.headline[2]}</span>
          </h1>
          <p className="mt-6 max-w-lg text-[17px] leading-relaxed text-cream/75 sm:text-[19px]">
            {hero.body}
          </p>

          <div className="mt-9">
            {eventLabel ?
              <p className="hub-label text-cream/60">{eventLabel}</p>
            : null}
            <div className={eventLabel ? 'mt-3' : undefined}>
              <Countdown
                key={nextDrop?.id ?? 'done'}
                targetIso={nextDrop?.liveAt ?? new Date(serverNow).toISOString()}
                serverNow={serverNow}
                units={countdown.units}
                doneLabel={hero.countdownDone}
                liveLabel={countdown.live}
                alreadyDone={allRevealed}
                onZero={handleZero}
              />
            </div>
          </div>

          <div className="mt-9 flex flex-wrap gap-3">
            <a
              href="#map"
              className="hub-display inline-flex min-h-[50px] items-center gap-2 bg-yamal px-7 text-[20px] tracking-[0.02em] text-coal transition-transform hover:-translate-y-0.5 active:translate-y-0"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="size-5"
                aria-hidden="true"
              >
                <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {hero.ctaMap}
            </a>
            <a
              href="#win"
              className="hub-display inline-flex min-h-[50px] items-center gap-2 border border-cream/35 px-7 text-[20px] tracking-[0.02em] text-cream transition-colors hover:border-yamal hover:text-yamal"
            >
              {hero.ctaWin}
            </a>
          </div>

          <a
            href="#map"
            className="hub-label mt-8 inline-flex min-h-[44px] items-center gap-2 self-start text-cream/55 transition-colors hover:text-yamal"
          >
            <span className="animate-bounce" aria-hidden="true">
              ↓
            </span>
            {hero.scroll}
          </a>
        </div>
      </div>
    </section>
  );
}
